'use client'

import { useEffect, useRef, useState } from 'react'
import { motion, useInView, useMotionValue, animate, useScroll, useTransform } from 'framer-motion'
import { cn } from '@/lib/utils'

export function Reveal({
  children,
  className,
  delay = 0,
  y = 20,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
  y?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.5, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export function Counter({
  to,
  suffix = '',
  prefix = '',
  decimals = 0,
  className,
}: {
  to: number
  suffix?: string
  prefix?: string
  decimals?: number
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const mv = useMotionValue(0)
  const [display, setDisplay] = useState('0')

  useEffect(() => {
    if (!inView) return
    const controls = animate(mv, to, {
      duration: 1.6,
      ease: 'easeOut',
      onUpdate: (v) => setDisplay(v.toLocaleString('en-US', { maximumFractionDigits: decimals, minimumFractionDigits: decimals })),
    })
    return () => controls.stop()
  }, [inView, to, mv, decimals])

  return (
    <span ref={ref} className={cn(className)}>
      {prefix}
      {display}
      {suffix}
    </span>
  )
}

export function ParallaxReveal({
  children,
  className,
  offset = 100,
}: {
  children: React.ReactNode
  className?: string
  offset?: number
}) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: false, margin: '-100px' })
  const { scrollY } = useScroll()
  const y = useTransform(scrollY, [0, 1000], [0, offset], { clamp: true })

  return (
    <motion.div
      ref={ref}
      style={inView ? { y } : { y: 0 }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

export function StaggerReveal({
  children,
  className,
  delay = 0,
  staggerDelay = 0.1,
}: {
  children: React.ReactNode[]
  className?: string
  delay?: number
  staggerDelay?: number
}) {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: staggerDelay,
        delayChildren: delay,
      },
    },
  }

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: staggerDelay * i },
    }),
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-80px' }}
      variants={container}
    >
      {Array.isArray(children) &&
        children.map((child, i) => (
          <motion.div key={i} variants={item}>
            {child}
          </motion.div>
        ))}
    </motion.div>
  )
}

export function BlurReveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, filter: 'blur(10px)' }}
      whileInView={{ opacity: 1, filter: 'blur(0px)' }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  )
}
